import { ProCard } from '@ant-design/pro-components'
import { sync } from '@app/icare/client/sync'
import {
  type MedicalTechnology,
  type MedicalTechnologyIdType,
  MedicalTechnologySchema,
} from '@app/icare/entities/medicalTechnology/schema/MedicalTechnology'
import { EntityDrawerShell } from '@app/icare/form/EntityDrawerShell'
import { AddressFieldGroup } from '@app/icare/form/group/AddressFieldGroup'
import { useEntityDrawer } from '@app/icare/hooks/useEntityDrawer'
import { t } from '@lingui/core/macro'
import { SectionFlow } from '@pkg/form/SectionFlow'
import type { SectionDef } from '@pkg/form/useSectionFlow'
import { zodDefaultInstance } from '@pkg/shared/form'
import { createTypedModal, type EditModalProps, type EditModalResult } from '@pkg/ui/modal'
import { Flex } from 'antd'

const sections = [
  { name: 'basic', title: t`Allgemein` },
  { name: 'misc', title: t`Sonstiges` },
] as const satisfies readonly SectionDef[]

type MedicalTechnologySection = (typeof sections)[number]['name']

type MedicalTechnologyEditDrawerProps =
  | (EditModalProps<MedicalTechnology, MedicalTechnologySection, MedicalTechnologyIdType> & {
      showGlobalImpactWarning?: boolean
    })
  | undefined

type MedicalTechnologyEditDrawerShow = (
  props?: MedicalTechnologyEditDrawerProps,
) => Promise<EditModalResult<MedicalTechnology>>

export const MedicalTechnologyEditDrawer = createTypedModal<
  MedicalTechnologyEditDrawerProps,
  EditModalResult<MedicalTechnology>,
  MedicalTechnologyEditDrawerShow
>(props => {
  const drawer = useEntityDrawer({
    schema: MedicalTechnologySchema,
    entityId: props?.id,
    sections,
    section: props?.section,
    formDefaults: {
      ...zodDefaultInstance(MedicalTechnologySchema),
      ...props?.defaults,
    },
    onPersist: async value => {
      await sync.mutate(value)
    },
  })

  const { form } = drawer

  return (
    <EntityDrawerShell drawer={drawer} showWarnings={props?.showGlobalImpactWarning}>
      <SectionFlow.Sections
        items={[
          {
            name: 'basic',
            title: t`Allgemein`,
            children: (
              <ProCard>
                <Flex vertical gap="small">
                  <form.AppField
                    name="name"
                    children={field => (
                      <field.Text label="Name" className="col-span-6" disabled={props?.showGlobalImpactWarning} />
                    )}
                  />

                  <AddressFieldGroup form={form} fields="address" />

                  <form.AppField name="contact" children={field => <field.ContactGroup hidePreferredCommunication />} />
                </Flex>
              </ProCard>
            ),
          },
          {
            name: 'misc',
            title: t`Sonstiges`,
            children: (
              <ProCard>
                <Flex vertical gap="small">
                  <form.AppField
                    name="comment"
                    children={field => <field.TextArea label={t`Bemerkungen`} className="grow" />}
                  />
                </Flex>
              </ProCard>
            ),
          },
        ]}
      />
    </EntityDrawerShell>
  )
})
